import type { Options } from 'redux-first-router';
import queryString from 'query-string';

import type { CartThunk, Dispatch, GetState } from 'model/State';
import validateRoute from 'routes/validateRoute';
import wrapRouteThunks from 'routes/wrapRouteThunks';
import { PRODUCT_LIST } from 'actions';
import initActionsRouteWrapper from 'actions/init';

import * as siteconfigurator from 'modules/siteconfigurator';
import * as addon from 'modules/addon';
import * as addtocart from 'modules/addtocart';
import * as contacts from 'modules/checkout/contacts';
import * as review from 'modules/checkout/review';
import * as payment from 'modules/checkout/payment';
import * as quote from 'modules/checkout/quote';
import * as complete from 'modules/checkout/complete';
import * as upgrade from 'modules/upgrade';
import * as cart from 'modules/cart';
import * as promotion from 'modules/promotion';
import * as renew from 'modules/renew';
import * as software from 'modules/software';
import * as mac from 'modules/mac';
import * as configure from 'modules/configure';
import * as marketplace from 'modules/marketplace';
import notificationFlagRouteWrapper from 'modules/meta/notificationFlag/routeWrapper';
import { languageInitWrapper } from 'modules/i18n/duck/actions';

export type Thunk = (dispatch: Dispatch, getState: GetState) => Promise<any>;

export type RouteDef = {
  name?: string;
  path: string;
  flag?: string;
  graduated?: boolean;
  thunk?: Thunk | CartThunk | void;
  pageOptions?: any;
  ufoName?: string;
};

export type RoutesMap = Record<string, RouteDef>;

export const routesMap: RoutesMap = {
  [PRODUCT_LIST]: {
    name: 'PRODUCT_LIST', // Name is used for segment events,
    path: '/',
  },
  [addon.actions.ROUTE]: {
    name: 'ADDON_CONFIGURATION',
    path: '/addon/:productKeys',
    thunk: addon.actions.load,
  },
  [addtocart.actions.ROUTE]: {
    path: '/addtocart',
    thunk: addtocart.actions.routeThunk(),
  },
  [configure.actions.ROUTE]: {
    name: 'PRODUCT_CONFIGURATION',
    path: '/product/:productKey',
    thunk: configure.actions.load,
  },
  [upgrade.landing.actions.ROUTE]: {
    name: 'UPGRADE',
    path: '/upgrade',
    thunk: upgrade.landing.actions.load,
  },
  [upgrade.tiers.actions.ROUTE]: {
    name: 'UPGRADE_TIERS',
    path: '/upgrade/tiers',
    thunk: upgrade.tiers.actions.load,
    ufoName: 'upgrade-tiers-route',
  },
  [upgrade.datacenter.actions.ROUTE]: {
    name: 'UPGRADE_DATACENTER',
    path: '/upgrade/datacenter',
    thunk: upgrade.datacenter.actions.load,
  },
  [contacts.actions.ROUTE]: {
    name: 'CONTACT',
    path: '/checkout/contact',
    thunk: contacts.actions.routeThunk(),
    ufoName: 'checkout-contacts-route',
  },
  [payment.actions.ROUTE]: {
    name: 'PAYMENT',
    path: '/checkout/payment',
    thunk: payment.actions.routeThunk(),
    pageOptions: { includesStripeCCForm: true },
  },
  [siteconfigurator.actions.ROUTE]: {
    name: 'SITECONFIG',
    path: '/siteconfigurator',
    thunk: (siteconfigurator.actions.routeThunk as unknown as () => CartThunk)(),
  },
  [review.actions.ROUTE]: {
    name: 'REVIEW',
    path: '/checkout/review',
    thunk: review.actions.load,
    pageOptions: { includesStripeCCForm: true },
  },
  [cart.actions.ROUTE]: {
    name: 'CART',
    path: '/cart',
    thunk: cart.actions.load,
  },
  [renew.landing.actions.ROUTE]: {
    name: 'RENEW',
    path: '/renew',
    thunk: renew.landing.actions.load,
  },
  [renew.server.actions.ROUTE]: {
    name: 'RENEW_SERVER',
    path: '/renew/server',
    thunk: renew.server.actions.load,
  },
  [complete.actions.ROUTE]: {
    name: 'COMPLETE',
    path: '/checkout/complete',
    pageOptions: { hideSecondaryMenu: true },
    thunk: complete.actions.load,
  },
  [quote.actions.ROUTE]: {
    name: 'QUOTE_DETAILS',
    path: '/checkout/quote',
    thunk: quote.actions.routeThunk(),
  },
  [marketplace.actions.ROUTE]: {
    path: '/marketplace',
    thunk: marketplace.actions.load(),
  },
  [promotion.actions.ROUTE]: {
    name: 'PROMOTION',
    path: '/promo',
    thunk: promotion.actions.load,
  },
  [software.buy.ROUTE]: {
    name: 'SOFTWARE_BUY',
    path: '/software/1/buy/:productKey',
    thunk: software.buy.load,
  },
  [software.renewAndUpgrade.RENEW_ROUTE]: {
    name: 'SOFTWARE_RENEW',
    path: '/software/1/renew/:accountId',
    thunk: software.renewAndUpgrade.load,
  },
  [software.renewAndUpgrade.UPGRADE_ROUTE]: {
    name: 'SOFTWARE_UPGRADE',
    path: '/software/1/upgrade/:accountId',
    thunk: software.renewAndUpgrade.load,
  },
  MIGRATE: {
    name: 'MIGRATE',
    path: '/price-comparison',
    pageOptions: { hideSecondaryMenu: true },
  },
  BUILDER: {
    name: 'BUILDER',
    path: '/price-comparison/builder',
    pageOptions: { hideSecondaryMenu: true },
  },
  [mac.landing.ROUTE]: {
    name: 'MAC_LICENSES',
    path: '/my',
    thunk: mac.landing.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true },
    ufoName: 'mac-route',
  },
  [mac.orders.ROUTE]: {
    name: 'MAC_ORDERS',
    path: '/my/billing/quotes',
    thunk: mac.orders.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true },
    ufoName: 'mac-orders-route',
  },
  [mac.ordersPreselected.ROUTE]: {
    name: 'MAC_ORDERS_PRESELECTED',
    path: '/my/billing/quotes/:orderId',
    thunk: mac.ordersPreselected.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true },
    ufoName: 'mac-orders-preselected-route',
  },
  [mac.invoices.ROUTE]: {
    name: 'MAC_INVOICES',
    path: '/my/billing/paidorders',
    thunk: mac.invoices.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true },
    ufoName: 'mac-invoices-route',
  },
  [mac.invoicesPreselected.ROUTE]: {
    name: 'MAC_INVOICES_PRESELECTED',
    path: '/my/billing/paidorders/:orderId',
    thunk: mac.invoicesPreselected.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true },
    ufoName: 'mac-invoices-preselected-route',
  },
  [mac.configUser.ROUTE]: {
    name: 'MAC_CONFIG_USER',
    path: '/my/config',
    thunk: mac.configUser.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true },
  },
  [mac.editDetails.ROUTE]: {
    name: 'MAC_EDIT_DETAILS',
    path: '/my/edit-details',
    thunk: mac.editDetails.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true },
  },
  [mac.licenseEvaluation.ROUTE]: {
    name: 'MAC_LICENSE_EVALUATION',
    path: '/my/license-evaluation',
    thunk: mac.licenseEvaluation.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true },
  },
  [mac.paymentDetails.ROUTE]: {
    name: 'MAC_PAYMENT_DETAILS',
    path: '/my/payment-details/:accountId',
    thunk: mac.paymentDetails.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true, includesStripeCCForm: true },
  },
  [mac.payment.ROUTE]: {
    name: 'MAC_PAYMENT',
    path: '/my/payment',
    thunk: mac.payment.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true },
  },
  [mac.updateCreditCard.ROUTE]: {
    name: 'MAC_UPDATE_CREDIT_CARD',
    path: '/my/update-credit-card/:accountId',
    thunk: mac.updateCreditCard.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true, includesStripeCCForm: true },
  },
  [mac.licensesPreselected.ROUTE]: {
    name: 'MAC_LICENSES_PRESELECTED',
    path: '/my/licenses/:senNumber',
    thunk: mac.licensesPreselected.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true },
    ufoName: 'mac-licenses-preselected-route',
  },
  [mac.updateServerId.ROUTE]: {
    name: 'MAC_UPDATE_SERVER_ID',
    path: '/my/products/edit-server-id/:accountId',
    thunk: mac.updateServerId.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true },
  },
  [mac.partners.ROUTE]: {
    name: 'MAC_PARTNERS',
    path: '/my/customers/partner',
    thunk: mac.partners.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true },
  },
  [mac.addonLicenseView.ROUTE]: {
    name: 'MAC_ADDON_LICENSE_VIEW',
    path: '/my/addon-license-view/:licenseId?',
    thunk: mac.addonLicenseView.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true },
  },
  [mac.addonLicenseEvaluation.ROUTE]: {
    name: 'MAC_ADDON_LICENSE_EVALUATION',
    path: '/my/addon/try/:addonKey',
    thunk: mac.addonLicenseEvaluation.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true },
  },
  [mac.enableAutoRenew.ROUTE]: {
    name: 'MAC_ENABLE_AUTO_RENEW',
    path: '/my/enable-auto-renew/:accountId',
    thunk: mac.enableAutoRenew.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true, includesStripeCCForm: true },
  },
  [mac.downloadSource.ROUTE]: {
    name: 'MAC_DOWNLOAD_SOURCE',
    path: '/my/download-source/:productKey',
    thunk: mac.downloadSource.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true },
  },
  [mac.emailRedirect.ROUTE]: {
    name: 'MAC_EMAIL_REDIRECT',
    path: '/my/email',
    thunk: mac.emailRedirect.load,
    pageOptions: { isMACPage: true, hideSecondaryMenu: true },
  },
};

export const basename = '/purchase';

export const routingOptions: Options = {
  basename,
  initialDispatch: false,
  querySerializer: queryString,
  onAfterChange: validateRoute,
};

export default wrapRouteThunks(
  initActionsRouteWrapper,
  languageInitWrapper,
  notificationFlagRouteWrapper,
)(routesMap);
